import React, { useState, useEffect } from 'react';
import { UserPlus, Mail, Loader2, Send, Clock, Check, Copy, Link2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { api } from '../services/api';

const inviteUrlFor = (inv) => {
  if (!inv?.token || typeof window === 'undefined') return null;
  return `${window.location.origin}/accept-invite?token=${encodeURIComponent(inv.token)}`;
};

const TeamPage = () => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);

  const loadInvites = async () => {
    setLoading(true);
    try {
      const list = await api.invites.list();
      setInvites(Array.isArray(list) ? list : []);
    } catch (err) {
      toast.error(err?.payload?.error || 'Failed to load invites.');
      setInvites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInvites();
  }, []);

  const copyLink = (url) => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => toast.success('Invite link copied.'));
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Enter an email address.');
      return;
    }
    setSending(true);
    try {
      const res = await api.invites.create(trimmed);
      const link = res?.inviteLink || null;
      setLastInviteLink(link);
      if (res?.emailSent) {
        toast.success(`Invite emailed to ${trimmed}`);
      } else {
        toast.success('Invite created. Copy the link below to share (email was not sent).');
      }
      setEmail('');
      loadInvites();
    } catch (err) {
      toast.error(err?.payload?.error || err?.message || 'Failed to send invite.');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { dateStyle: 'short' }) + ' ' + d.toLocaleTimeString(undefined, { timeStyle: 'short' });
  };

  const isPending = (inv) => !inv.usedAt && new Date(inv.expiresAt) > new Date();

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Team & invites</h1>
        <p className="mt-1 text-gray-600">Invite people by email. They’ll get a link to set their password and join.</p>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Send an invite</h2>
        <form onSubmit={handleSendInvite} className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[200px]">
            <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700 mb-1">
              Email address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                id="invite-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@example.com"
                className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                disabled={sending}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={sending}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:shadow-md disabled:opacity-60"
          >
            {sending ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            Send invite
          </button>
        </form>
        {lastInviteLink && (
          <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-3">
            <p className="flex items-center gap-2 text-sm font-medium text-indigo-900">
              <Link2 size={16} /> Share this invite link
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                readOnly
                value={lastInviteLink}
                className="min-w-0 flex-1 rounded border border-indigo-200 bg-white px-3 py-2 text-xs text-gray-700"
              />
              <button
                type="button"
                onClick={() => copyLink(lastInviteLink)}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Invites</h2>
          <p className="text-sm text-gray-500 mt-0.5">Pending invites expire in 7 days.</p>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="animate-spin" size={32} />
          </div>
        ) : invites.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <UserPlus className="mx-auto mb-3 text-gray-300" size={48} />
            <p>No invites yet. Send one above.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {invites.map((inv) => {
              const pending = isPending(inv);
              const link = pending ? inviteUrlFor(inv) : null;
              return (
              <li key={inv.id} className="px-6 py-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600">
                    <Mail size={20} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{inv.email}</p>
                    <p className="text-sm text-gray-500">
                      {pending ? (
                        <>Expires {formatDate(inv.expiresAt)}</>
                      ) : (
                        <>Used {inv.usedAt ? formatDate(inv.usedAt) : 'Expired'}</>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {pending && link && (
                    <button
                      type="button"
                      onClick={() => copyLink(link)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      title="Copy invite link"
                    >
                      <Copy size={12} /> Copy link
                    </button>
                  )}
                  {pending ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800">
                      <Clock size={12} /> Pending
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                      <Check size={12} /> {inv.usedAt ? 'Accepted' : 'Expired'}
                    </span>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default TeamPage;
